import { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import logo from "../../assets/logo.png";
import { useCartStore } from "../../store/cartStore";
import { usePixel } from "../../hooks/usePixel";

/* ─────────────────────────────────────────
   COUNTDOWN HOOK
───────────────────────────────────────── */
function useCountdown(expiresAt) {
  const getSecondsLeft = useCallback(() => {
    const diff = new Date(expiresAt).getTime() - Date.now();
    return Math.max(0, Math.floor(diff / 1000));
  }, [expiresAt]);

  const [secondsLeft, setSecondsLeft] = useState(getSecondsLeft);

  useEffect(() => {
    if (!expiresAt) return;
    const interval = setInterval(() => {
      const s = getSecondsLeft();
      setSecondsLeft(s);
      if (s <= 0) clearInterval(interval);
    }, 1000);
    return () => clearInterval(interval);
  }, [expiresAt, getSecondsLeft]);

  const minutes = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const seconds = String(secondsLeft % 60).padStart(2, "0");
  const expired = secondsLeft <= 0;
  const urgent = secondsLeft <= 60 && secondsLeft > 0;

  return { minutes, seconds, expired, urgent };
}

/* ─────────────────────────────────────────
   STRIPE APPEARANCE
───────────────────────────────────────── */
const stripeAppearance = {
  theme: "night",
  variables: {
    colorPrimary: "#22c55e",
    colorBackground: "#031c14",
    colorText: "#ffffff",
    colorDanger: "#ef4444",
    fontFamily: "inherit",
    borderRadius: "12px",
    colorInputBackground: "#021610",
    colorInputBorder: "rgba(20,83,45,0.5)",
  },
  rules: {
    ".Input": {
      border: "1px solid rgba(20,83,45,0.5)",
      backgroundColor: "#021610",
    },
    ".Input:focus": {
      border: "1px solid rgba(34,197,94,0.6)",
      boxShadow: "none",
    },
    ".Label": {
      color: "rgba(156,163,175,1)",
      fontSize: "10px",
      letterSpacing: "0.1em",
      textTransform: "uppercase",
    },
  },
};

/* ─────────────────────────────────────────
   PAYMENT FORM (inner — uses Stripe hooks)
───────────────────────────────────────── */
function PaymentForm({ orderNumber, buyerEmail, pixels, trackEvent, total, currency, onExpired }) {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const clearCart = useCartStore((s) => s.clearCart);

  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState(null);

  const handlePay = async () => {
    if (!stripe || !elements) return;

    setPayError(null);
    setPaying(true);

    trackEvent("AddPaymentInfo", {
      value: total,
      currency: currency,
    });

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        receipt_email: buyerEmail,
      },
      redirect: "if_required", // avoid full redirect for card payments
    });

    if (error) {
      setPayError(error.message);
      setPaying(false);
      return;
    }

    if (paymentIntent && paymentIntent.status === "succeeded") {
      clearCart();
      navigate("/tickets-store/order-status", {
        state: { orderNumber, pixels, },
      });
    } else {
      setPayError("El pago no se completó. Inténtalo de nuevo.");
      setPaying(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* STRIPE PAYMENT ELEMENT */}
      <PaymentElement
        options={{
          layout: "tabs",
          paymentMethodOrder: ["card"],
        }}
      />

      {/* PAY ERROR */}
      {payError && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3">
          <p className="text-sm text-red-400">{payError}</p>
        </div>
      )}

      {/* PAY BUTTON */}
      <button
        onClick={handlePay}
        disabled={paying || !stripe || !elements}
        className={`w-full py-4 rounded-xl font-bold tracking-widest uppercase text-sm transition
          ${
            paying || !stripe
              ? "bg-green-700 text-black/50 cursor-not-allowed"
              : "bg-green-500 text-black hover:bg-green-400"
          }`}
      >
        {paying ? "Procesando pago..." : "Pagar ahora →"}
      </button>

      <p className="text-center text-[10px] text-gray-600 tracking-wider uppercase">
        🔒 Pago seguro con encriptación SSL
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────
   PAYMENT PAGE (outer)
───────────────────────────────────────── */
export default function Payment() {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state;

  const {
    orderNumber,
    pixels,
    total,
    currency,
    expiresAt,
    clientSecret,
    stripePublicKey,
    buyerEmail,
  } = state;

  const { trackEvent } = usePixel(pixels || []);

  const { minutes, seconds, expired, urgent } = useCountdown(expiresAt);

  // Load Stripe lazily with the key from the backend
  const [stripePromise] = useState(() => loadStripe(stripePublicKey));

  // If landed here without order state — redirect back
  if (!state?.clientSecret || !state?.stripePublicKey) {
    navigate("/tickets-store/");
    return null;
  }

  const formatCurrency = (amount, cur) => {
    const map = { EUR: "€", USD: "$", GBP: "£" };
    return `${map[cur] || cur} ${parseFloat(amount).toFixed(2)}`;
  };

  return (
    <div className="bg-[#021610] text-white min-h-screen">
      {/* NAVBAR */}
      <div className="fixed top-0 left-0 w-full bg-black/80 backdrop-blur-md z-20 flex items-center justify-between px-6 py-2">
        <button onClick={() => navigate("/tickets-store/")}>
          <img
            src={logo}
            alt="Afrodise"
            className="h-6 md:h-8 object-contain hover:opacity-80 transition"
          />
        </button>

        {/* COUNTDOWN */}
        {expiresAt && (
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-mono tracking-widest transition
              ${
                expired
                  ? "border-red-500/50 bg-red-500/10 text-red-400"
                  : urgent
                    ? "border-yellow-500/50 bg-yellow-500/10 text-yellow-400"
                    : "border-green-900/40 bg-green-900/10 text-green-400"
              }`}
          >
            <span className="text-[10px] uppercase tracking-widest mr-1 hidden sm:block">
              Reserva expira en
            </span>
            {expired ? "Expirado" : `${minutes}:${seconds}`}
          </div>
        )}
      </div>

      <div className="max-w-xl mx-auto px-4 pt-24 pb-16">
        {/* STEPPER */}
        <div className="flex items-center w-full text-xs text-green-400/70 mb-10">
          <StepDot number="1" label="TIPO DE ENTRADA" done />
          <StepLine />
          <StepDot number="2" label="CONFIRMAR" done />
          <StepLine />
          <StepDot number="3" label="PAGO" active />
        </div>

        <p className="text-[10px] tracking-widest text-green-400/70 uppercase">
          PASO 3
        </p>
        <h1 className="text-3xl md:text-4xl font-bold mt-1 mb-2">
          Completa el pago
        </h1>

        {/* ORDER REF + TOTAL */}
        <div className="flex items-center justify-between mt-4 mb-8 bg-[#031c14] border border-green-900/30 rounded-xl px-4 py-3">
          <div>
            <p className="text-[10px] tracking-widest text-gray-400 uppercase">
              Pedido
            </p>
            <p className="text-sm font-mono text-green-400 mt-0.5">
              #{orderNumber}
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] tracking-widest text-gray-400 uppercase">
              Total
            </p>
            <p className="text-xl font-bold text-green-400 mt-0.5">
              {formatCurrency(total, currency)}
            </p>
          </div>
        </div>

        {/* EXPIRED STATE */}
        {expired ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-6 text-center space-y-3">
            <p className="text-2xl">⏰</p>
            <p className="font-semibold text-red-400">Reserva expirada</p>
            <p className="text-sm text-gray-400">
              Tu reserva de entradas ha expirado. Vuelve a seleccionar tus
              entradas para continuar.
            </p>
            <button
              onClick={() => navigate("/tickets-store/")}
              className="mt-2 px-6 py-2 rounded-xl border border-green-500 text-green-400 text-sm hover:bg-green-500 hover:text-black transition"
            >
              Volver a eventos
            </button>
          </div>
        ) : (
          /* STRIPE PAYMENT FORM */
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: stripeAppearance,
            }}
          >
            <PaymentForm
              orderNumber={orderNumber}
              buyerEmail={buyerEmail}
              pixels={pixels}
              trackEvent={trackEvent}
              total={total}
              currency={currency}
              onExpired={() => {}}
            />
          </Elements>
        )}
      </div>
    </div>
  );
}

/* ── STEPPER ── */

function StepLine() {
  return <div className="flex-1 h-px bg-green-900/30 mx-3" />;
}

function StepDot({ number, label, active, done }) {
  return (
    <div className="flex items-center gap-2 shrink-0">
      <div
        className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-semibold
          ${done ? "bg-green-700 text-white" : ""}
          ${active ? "bg-green-500 text-black" : ""}
          ${!done && !active ? "border border-green-500/30 text-green-400" : ""}
        `}
      >
        {done ? "✓" : number}
      </div>
      <span className="hidden md:block whitespace-nowrap text-[10px] tracking-widest">
        {label}
      </span>
    </div>
  );
}
